import { defineStore } from "pinia";
import _ from "lodash";
import {
    FetchState,
    PartnerHsaStoreInfo,
    SupportInfo,
    SupportInfoRequest,
    PortalCustomizationState,
    CommunicationKitType,
    AssetType,
    AssetRequest
} from "@/shared/types";
import * as UserService from "@/shared/api/userService";
import userResource from "@/shared/resource/user";
import * as storeHelper from "@/shared/storeHelper";
import { useUserStore } from "@/shared/store/user";
import { useNotifierStore } from "@/shared/store/notifier";
import { isMobileApp } from "@/shared/mobileHelper";
import { Config } from "@/shared/config";
import { isNullEmptyOrUndefined } from "@/shared/stringHelper";

export const initialState: PortalCustomizationState = {
    partnerLogo: "",
    employerLogo: "",
    fetchState: FetchState.Uninitialized,
    fetchEmployerLogoState: FetchState.Uninitialized,
    isEmployerCustomLogo: false,
    isPartnerCustomLogo: false,
    debitCardImage: "",
    isCustomDebitCardImage: false,
    debitCardFetchState: FetchState.Uninitialized,
    partnerId: 0,
    employerId: 0,
    supportInfo: {
        supportPhone: "",
        supportEmail: "",
        supportWebsite: "",
        website: "",
        displayName: "",
        customFromEmail: "",
        emailHeaderImageUrl: "",
        emailButtonColor: "",
        emailButtonTextColor: "",
        isPrimaryContactServiceContact: false,
        primaryContactTitleOverride: "",
        primaryContactHoursConfig: "hide",
        primaryContactCustomHours: "",
        secondaryContactPhone: "",
        secondaryContactShowPhone: false,
        secondaryContactEmail: "",
        secondaryContactTitle: "",
        secondaryContactHoursConfig: "hide",
        secondaryContactCustomHours: "",
        partnerId: 0,
        employerId: 0,
        fetchState: FetchState.Uninitialized,
        isFallbackData: false
    },
    hsaStoreFetchState: FetchState.Uninitialized,
    hsaStoreInfo: {
        storeLink: "",
        eligibilityLink: "",
        storeType: null
    },
    communicationKitType: CommunicationKitType.None,
    communicationKitTypeFetchState: FetchState.Uninitialized,
    employeeLogoutRedirectUrl: null,
    employeeLogoutRedirectFetchState: FetchState.Uninitialized
};

const defaultBendSupportInfo: SupportInfo = {
    supportPhone: "888-284-7551",
    supportEmail: "support@bendhsa.com",
    supportWebsite: "http://www.bendhsa.com/bend-hsa-about",
    website: "http://www.bendhsa.com/bend-hsa-about",
    displayName: "Bend",
    customFromEmail: "",
    emailHeaderImageUrl: "",
    emailButtonColor: "",
    emailButtonTextColor: "",
    isPrimaryContactServiceContact: false,
    primaryContactTitleOverride: "",
    primaryContactHoursConfig: "hide",
    primaryContactCustomHours: "",
    secondaryContactPhone: "",
    secondaryContactShowPhone: false,
    secondaryContactEmail: "",
    secondaryContactTitle: "",
    secondaryContactHoursConfig: "hide",
    secondaryContactCustomHours: "",
};

const defaultHsaBankSupportInfo: SupportInfo = {
    supportPhone: "800-357-6246",
    supportEmail: "askus@hsabank.com",
    supportWebsite: "https://www.hsabank.com/hsabank/About-Us/About-HSA-Bank",
    website: "https://www.hsabank.com/hsabank/About-Us/About-HSA-Bank",
    displayName: "HSA Bank",
    customFromEmail: "HSA_Bank@hsabank.com",
    emailHeaderImageUrl: "",
    emailButtonColor: "#3e5d57",
    emailButtonTextColor: "#ffffff",
    isPrimaryContactServiceContact: false,
    primaryContactTitleOverride: "",
    primaryContactHoursConfig: "hide",
    primaryContactCustomHours: "",
    secondaryContactPhone: "",
    secondaryContactShowPhone: false,
    secondaryContactEmail: "",
    secondaryContactTitle: "",
    secondaryContactHoursConfig: "hide",
    secondaryContactCustomHours: "",
};

// Alex HSA
const defaultJellyvisionSupportInfo: SupportInfo = {
    supportPhone: "833-223-5601",
    supportEmail: "askalex@hsabank.com",
    supportWebsite: "https://www.hsabank.com/hsabank/About-Us/About-HSA-Bank",
    website: "https://www.hsabank.com/hsabank/About-Us/About-HSA-Bank",
    displayName: "HSA Bank",
    customFromEmail: "HSA_Bank@hsabank.com",
    emailHeaderImageUrl: "",
    emailButtonColor: "#3e5d57",
    emailButtonTextColor: "#ffffff",
    isPrimaryContactServiceContact: false,
    primaryContactTitleOverride: "",
    primaryContactHoursConfig: "hide",
    primaryContactCustomHours: "",
    secondaryContactPhone: "",
    secondaryContactShowPhone: false,
    secondaryContactEmail: "",
    secondaryContactTitle: "",
    secondaryContactHoursConfig: "hide",
    secondaryContactCustomHours: "",
};

export const usePortalCustomizationStore = defineStore("portalCustomization", {
    persist: {
        storage: localStorage,
        paths: storeHelper.getStoragePaths("portalCustomization"),
    },
    state: () => (_.cloneDeep(initialState)),
    getters: {
        partnerLogoSrc: state => state.partnerLogo,
        employerLogoSrc: state => state.employerLogo,
        isLoaded: state => state.fetchState === FetchState.Loaded,
        isEmployerLogoLoaded: state => state.fetchEmployerLogoState === FetchState.Loaded,
        isDebitCardLoaded: state => state.debitCardFetchState === FetchState.Loaded,
        debitCardImageSrc: state => state.debitCardImage,
        isPartnerCustomizationEnabled: state => state.partnerId && state.partnerId !== 0,
        isSupportInfoLoaded: state => state.supportInfo.fetchState === FetchState.Loaded,
        isHsaStoreInfoLoaded: state => state.hsaStoreFetchState === FetchState.Loaded,
        partnerSupportPhone: state => state.supportInfo.supportPhone,
        hasPartnerSupportEmail: state => state.supportInfo !== null
            && !isNullEmptyOrUndefined(state.supportInfo.supportEmail),
        displayResourcesNavigationLink: state => state.communicationKitType === CommunicationKitType.BendBranded,
        defaultDesktopSupportInfo: (): SupportInfo =>  {
            return document.location.hostname.indexOf("hsabank.com") > -1 ?
              defaultHsaBankSupportInfo
              : defaultBendSupportInfo;
        },
        defaultMobileSupportInfo: (): SupportInfo => {
            if (Config.Mobile.PartnerId && Number(Config.Mobile.PartnerId) === 15) {
                return defaultHsaBankSupportInfo;
            }
            else if (Config.Mobile.PartnerId && Number(Config.Mobile.PartnerId) === 32) {
                return defaultJellyvisionSupportInfo;
            }
            else {
                return defaultBendSupportInfo;
            }
        }
    },
    actions: {
        setPartner (partner: number) {
            this.partnerId = partner;
            const userStore = useUserStore();
            if (userStore.isEmployerPortal) {
                this.getSupportInfo(
                  { partnerId: this.partnerId, employerId: userStore.employerId } );
            }
        },
        setEmployer (employerId: number) {
            this.employerId = employerId;
        },
        setPartnerLogoSrc (src: string) {
            this.partnerLogo = src;
        },
        setEmployerLogoSrc (src: string) {
            this.employerLogo = src;
        },
        resetEmployerLogo () {
            this.isEmployerCustomLogo = false;
            this.employerLogo = "";
            this.fetchEmployerLogoState = FetchState.Uninitialized;
        },
        setDebitCardImageSrc (src: string) {
            this.debitCardImage = src;
        },
        setLogoFetchState (fetchState: FetchState) {
            this.fetchState = fetchState;
        },
        setEmployerLogoFetchState (fetchState: FetchState) {
            this.fetchEmployerLogoState = fetchState;
        },
        setDebitCardFetchState (fetchState: FetchState) {
            this.debitCardFetchState = fetchState;
        },
        setEmployerCustomLogoStatus (status: boolean) {
            this.isEmployerCustomLogo = status;
        },
        setPartnerCustomLogoStatus (status: boolean) {
            this.isPartnerCustomLogo = status;
        },
        setCustomDebitCardImageStatus (status: boolean) {
            this.isCustomDebitCardImage = status;
        },
        setSupportInfo (supportInfo: SupportInfo) {
            this.supportInfo.supportEmail = supportInfo.supportEmail || "";
            this.supportInfo.supportPhone = supportInfo.supportPhone || "";
            this.supportInfo.supportWebsite = supportInfo.supportWebsite || "";
            this.supportInfo.website = supportInfo.website || "";
            this.supportInfo.displayName = supportInfo.displayName || "";
            this.supportInfo.customFromEmail = supportInfo.customFromEmail || "";
            this.supportInfo.emailHeaderImageUrl = supportInfo.emailHeaderImageUrl || "";
            this.supportInfo.emailButtonTextColor = supportInfo.emailButtonTextColor || "";
            this.supportInfo.emailButtonColor = supportInfo.emailButtonColor || "";
            this.supportInfo.isPrimaryContactServiceContact = supportInfo.isPrimaryContactServiceContact || false;
            this.supportInfo.primaryContactTitleOverride = supportInfo.primaryContactTitleOverride || "";
            this.supportInfo.primaryContactHoursConfig = supportInfo.primaryContactHoursConfig || "";
            this.supportInfo.primaryContactCustomHours = supportInfo.primaryContactCustomHours || "";
            this.supportInfo.secondaryContactPhone = supportInfo.secondaryContactPhone || "";
            this.supportInfo.secondaryContactShowPhone = supportInfo.secondaryContactShowPhone || false;
            this.supportInfo.secondaryContactEmail = supportInfo.secondaryContactEmail || "";
            this.supportInfo.secondaryContactTitle = supportInfo.secondaryContactTitle || "";
            this.supportInfo.secondaryContactHoursConfig = supportInfo.secondaryContactHoursConfig || "";
            this.supportInfo.secondaryContactCustomHours = supportInfo.secondaryContactCustomHours || "";
        },
        setSupportInfoFetchState (fetchState: FetchState) {
            this.supportInfo.fetchState = fetchState;
        },
        setSupportInfoPartner (partnerId: number) {
            this.supportInfo.partnerId = partnerId;
        },
        setSupportInfoEmployer (employerId: number) {
            this.supportInfo.employerId = employerId;
        },
        resetCustomizationState () {
            this.supportInfo = initialState.supportInfo;
            this.partnerLogo = initialState.partnerLogo;
            this.employerLogo = initialState.employerLogo;
            this.isPartnerCustomLogo = initialState.isPartnerCustomLogo;
            this.isEmployerCustomLogo = initialState.isEmployerCustomLogo;
            this.debitCardImage = initialState.debitCardImage;
            this.isCustomDebitCardImage = initialState.isCustomDebitCardImage;
            this.setPartner(initialState.partnerId);
            this.setEmployer(initialState.employerId);
            this.fetchState = FetchState.Loaded;
            this.fetchEmployerLogoState = FetchState.Loaded;
            this.debitCardFetchState = FetchState.Loaded;
        },
        invalidateSupportInfoState () {
            this.supportInfo = _.cloneDeep(initialState.supportInfo);
        },
        setSupportInfoFallbackDataFlag (isFallbackData: boolean) {
            this.supportInfo.isFallbackData = isFallbackData;
        },
        setHsaStoreFetchState (fetchState: FetchState) {
            this.hsaStoreFetchState = fetchState;
        },
        setHsaStoreInfo (info: PartnerHsaStoreInfo) {
            this.hsaStoreInfo.storeLink = info.storeLink;
            this.hsaStoreInfo.eligibilityLink = info.eligibilityLink;
            this.hsaStoreInfo.storeType = info.storeType;
        },
        unsetHsaStoreLoaded () {
            this.hsaStoreFetchState = FetchState.Uninitialized;
        },
        setEmployeeLogoutRedirectUrl (employeeLogoutRedirectUrl: string) {
            this.employeeLogoutRedirectUrl = employeeLogoutRedirectUrl;
        },
        setEmployeeLogoutRedirectFetchState (fetchState: FetchState) {
            this.employeeLogoutRedirectFetchState = fetchState;
        },
        setCommunicationKitType (communicationKitType: CommunicationKitType) {
            this.communicationKitType = communicationKitType;
        },
        setCommunicationKitTypeFetchState (fetchState: FetchState) {
            this.communicationKitTypeFetchState = fetchState;
        },
        setDefaultSupportInfo () {
            isMobileApp() ?
                this.setSupportInfo(this.defaultMobileSupportInfo)
                : this.setSupportInfo(this.defaultDesktopSupportInfo);

            this.setSupportInfoFallbackDataFlag(true);
            this.setSupportInfoFetchState(FetchState.Loaded);
        },
        getUserEmployerLogo ({ employerId, partnerId }: AssetRequest) {
            if ((this.fetchEmployerLogoState !== FetchState.Uninitialized && this.employerId === employerId)) {
                return;
            }
            this.setEmployerLogoFetchState(FetchState.Loading);

            return UserService.getEmployerAsset(employerId, AssetType.Logo)
                .then(resp => {
                    const src = userResource.employerAsset(employerId, AssetType.Logo);
                    this.setEmployerLogoSrc(src);
                    this.setEmployerLogoFetchState(FetchState.Loaded);
                    this.setEmployerCustomLogoStatus(true);
                })
                .catch(err => {
                    this.setLogoFetchState(FetchState.Loaded);
                    this.setEmployerLogoFetchState(FetchState.Loaded);
                    this.setEmployerLogoSrc("");
                    this.setEmployerCustomLogoStatus(false);
                });
        },
        getUserLogo (partnerId) {
            if ((this.fetchState !== FetchState.Uninitialized &&
                (this.partnerId === partnerId))) {
                return;
            }

            this.setLogoFetchState(FetchState.Loading);

            return UserService.getUserAsset(partnerId, 0, AssetType.Logo)
                .then(resp => {
                    const src = userResource.userAsset(partnerId, 0, AssetType.Logo);
                    this.setPartnerLogoSrc(src);
                    this.setPartnerCustomLogoStatus(true);
                    this.setPartner(partnerId);
                    this.setLogoFetchState(FetchState.Loaded);
                })
                .catch(err => {
                    this.setPartnerLogoSrc("");
                    this.setPartnerCustomLogoStatus(false);
                    this.setPartner(partnerId);
                    this.setLogoFetchState(FetchState.Loaded);
                });
        },
        getPartnerCustomLogo (partnerId: number) {
            if (this.fetchState !== FetchState.Uninitialized && this.partnerId === partnerId) {
                return;
            }
            this.setLogoFetchState(FetchState.Loading);
            return UserService.getPartnerAsset(partnerId, AssetType.Logo)
                .then(resp => {
                    const src = userResource.partnerAsset(partnerId, AssetType.Logo);
                    this.setPartnerLogoSrc(src);
                    this.setPartner(partnerId);
                    this.setLogoFetchState(FetchState.Loaded);
                    this.setPartnerCustomLogoStatus(true);
                })
                .catch(err => {
                    this.setLogoFetchState(FetchState.Loaded);
                    this.setPartnerLogoSrc("");
                    this.setPartner(partnerId);
                    this.setPartnerCustomLogoStatus(false);
                });
        },
        getEmployerCustomLogo (employerId: number) {
            if (this.fetchEmployerLogoState !== FetchState.Uninitialized && this.employerId === employerId) {
                return;
            }
            this.setEmployerLogoFetchState(FetchState.Loading);
            return UserService.getEmployerAsset(employerId, AssetType.Logo)
                .then(resp => {
                    const src = userResource.employerAsset(employerId, AssetType.Logo);
                    this.setEmployerLogoSrc(src);
                    this.setEmployer(employerId);
                    this.setEmployerLogoFetchState(FetchState.Loaded);
                    this.setEmployerCustomLogoStatus(true);
                })
                .catch(err => {
                    this.setEmployerLogoFetchState(FetchState.Loaded);
                    this.setEmployerLogoSrc("");
                    this.setEmployer(employerId);
                    this.setEmployerCustomLogoStatus(false);
                });
        },
        getCustomDebitCardImage (partnerId: number) {
            if (this.debitCardFetchState !== FetchState.Uninitialized && this.partnerId === partnerId) {
                return;
            }
            this.setDebitCardFetchState(FetchState.Loading);
            return UserService.getPartnerAsset(partnerId, AssetType.DebitCardImage)
                .then(resp => {
                    const src = userResource.partnerAsset(partnerId, AssetType.DebitCardImage);
                    this.setDebitCardImageSrc(src);
                    this.setPartner(partnerId);
                    this.setDebitCardFetchState(FetchState.Loaded);
                    this.setCustomDebitCardImageStatus(true);
                })
                .catch(err => {
                    this.setDebitCardFetchState(FetchState.Loaded);
                    this.setDebitCardImageSrc("");
                    this.setPartner(partnerId);
                    this.setCustomDebitCardImageStatus(false);
                });
        },
        savePartnerCustomLogo ({ partnerId, logoFile }) {
            return UserService.savePartnerAsset(partnerId, AssetType.Logo, logoFile)
                .then(resp => {
                    const src = userResource.partnerAsset(partnerId, AssetType.Logo);
                    this.setPartnerLogoSrc(src);
                    this.setPartner(partnerId);
                    this.setLogoFetchState(FetchState.Loaded);
                    this.setPartnerCustomLogoStatus(true);
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: false }));
        },
        deletePartnerCustomLogo (partnerId: number) {
            return UserService.deletePartnerAsset(partnerId, AssetType.Logo)
                .then(resp => {
                    this.setPartnerLogoSrc("");
                    this.setPartner(partnerId);
                    this.setLogoFetchState(FetchState.Loaded);
                    this.setPartnerCustomLogoStatus(false);
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: false }));
        },
        saveEmployerCustomLogo ({ employerId, logoFile }) {
            return UserService.saveEmployerAsset(employerId, AssetType.Logo, logoFile)
                .then(resp => {
                    const src = userResource.employerAsset(employerId, AssetType.Logo);
                    this.setEmployerLogoSrc(src);
                    this.setEmployer(employerId);
                    this.setEmployerLogoFetchState(FetchState.Loaded);
                    this.setEmployerCustomLogoStatus(true);
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: false }));
        },
        deleteEmployerCustomLogo (employerId: number) {
            return UserService.deleteEmployerAsset(employerId, AssetType.Logo)
                .then(resp => {
                    this.setEmployerLogoFetchState(FetchState.Loaded);
                    this.setEmployerLogoSrc("");
                    this.setEmployer(employerId);
                    this.setEmployerCustomLogoStatus(false);
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: false }));
        },
        saveCustomDebitCardImage ({ partnerId, logoFile }) {
            return UserService.savePartnerAsset(partnerId, AssetType.DebitCardImage, logoFile)
                .then(resp => {
                    const src = userResource.partnerAsset(partnerId, AssetType.DebitCardImage);
                    this.setDebitCardImageSrc(src);
                    this.setPartner(partnerId);
                    this.setDebitCardFetchState(FetchState.Loaded);
                    this.setCustomDebitCardImageStatus(true);
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: false }));
        },
        deleteDebitCardImage (partnerId: number) {
            return UserService.deletePartnerAsset(partnerId, AssetType.DebitCardImage)
                .then(resp => {
                    this.setDebitCardImageSrc("");
                    this.setPartner(partnerId);
                    this.setDebitCardFetchState(FetchState.Loaded);
                    this.setCustomDebitCardImageStatus(false);
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: false }));
        },
        getSupportInfo ({ partnerId, employerId }: SupportInfoRequest) {
            if (this.supportInfo.fetchState !== FetchState.Uninitialized
                && !this.supportInfo.isFallbackData
                && partnerId === this.supportInfo.partnerId
                && employerId === this.supportInfo.employerId) {
                return;
            }
            this.invalidateSupportInfoState();
            this.setSupportInfoFetchState(FetchState.Loading);
            return UserService.getSupportInfo(partnerId || 0, employerId || 0)
                .then(resp => {
                    this.setSupportInfo(resp.data);
                    this.setSupportInfoPartner(partnerId);
                    this.setSupportInfoEmployer(employerId);
                    this.setSupportInfoFallbackDataFlag(false);
                    this.setSupportInfoFetchState(FetchState.Loaded);
                })
                .catch(err => {
                    this.setDefaultSupportInfo();
                });
        },
        savePartnerSupportInfo ({ partnerId, supportInfo }: any) {
            const notifierStore = useNotifierStore();
            return UserService.savePartnerSupportInfo(partnerId, supportInfo)
                .then(resp => {
                    this.setSupportInfo(supportInfo);
                    this.setSupportInfoPartner(partnerId);
                    notifierStore.setSuccess("partner support info saved.", notifierStore.shortTimeout);
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: false }));
        },
        saveEmployerSupportInfo ({ employerId, supportInfo }: any) {
            const notifierStore = useNotifierStore();
            return UserService.saveEmployerSupportInfo(employerId, supportInfo)
                .then(resp => {
                    this.setSupportInfo(supportInfo);
                    this.setSupportInfoEmployer(employerId);
                    notifierStore.setSuccess("employer support info saved.");
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: false }));
        },
        resetCustomization () {
            this.resetCustomizationState();
            this.setDefaultSupportInfo();
        },
        getHsaStoreInfo (partnerId: number) {
            if (this.hsaStoreFetchState !== FetchState.Uninitialized) {
                return;
            }
            this.setHsaStoreFetchState(FetchState.Loading);
            return UserService.getHsaStoreInfo(partnerId)
                .then(r => {
                    this.setHsaStoreInfo(r.data);
                    this.setHsaStoreFetchState(FetchState.Loaded);
                })
                .catch(() => {
                    this.setHsaStoreFetchState(FetchState.Loaded);
                });
        },
        saveHsaStoreInfo ({ partnerId, info }) {
            const notifierStore = useNotifierStore();
            return UserService.savePartnerStoreInfo(partnerId, info)
                .then(r => {
                    notifierStore.setSuccess("Marketplace info saved.");
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: false }));
        },
        getEmployeeLogoutRedirectUrl (partnerId: number) {
            if (this.employeeLogoutRedirectFetchState !== FetchState.Uninitialized) {
                return;
            }
            const userStore = useUserStore();
            if (userStore.isEmployerPortal) {
                this.setEmployeeLogoutRedirectFetchState(FetchState.Loaded);
                return;
            }

            this.setEmployeeLogoutRedirectFetchState(FetchState.Loading);
            return UserService.getEmployeeLogoutRedirect(partnerId)
                .then(r => {
                    this.setEmployeeLogoutRedirectUrl(r.data);
                    this.setEmployeeLogoutRedirectFetchState(FetchState.Loaded);
                })
                .catch(() => {
                    this.setEmployeeLogoutRedirectFetchState(FetchState.Loaded);
                });
        },
        getCommunicationKitType (partnerId: number) {
            if (this.communicationKitTypeFetchState === FetchState.Loading) {
                return;
            }
            const userStore = useUserStore();
            if (!userStore.isEmployerPortal) {
                this.setCommunicationKitTypeFetchState(FetchState.Loaded);
                return;
            }

            if (partnerId < 1) {
                return;
            }

            this.setCommunicationKitTypeFetchState(FetchState.Loading);
            return UserService.getCommunicationKitType(partnerId)
                .then(r => {
                    this.setCommunicationKitType(r.data);
                    this.setCommunicationKitTypeFetchState(FetchState.Loaded);
                })
                .catch(() => {
                    this.setCommunicationKitTypeFetchState(FetchState.Loaded);
                });
        },
    }
});